import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'pwa_install_dismissed';

export default function InstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't show if the user already dismissed it
    if (sessionStorage.getItem(DISMISSED_KEY)) return;

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="banner"
      className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-[var(--z-toast)] animate-fade-in-up"
    >
      <div className="bg-bg-elevated border border-border-default rounded-lg shadow-xl px-4 py-3 flex items-center gap-3">
        <div className="flex-shrink-0 w-8 h-8 bg-accent-primary/10 rounded-md flex items-center justify-center">
          <Download className="w-4 h-4 text-accent-primary" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm text-text-primary font-medium leading-snug">
            {t('pwa.install.banner')}
          </p>
          <button
            onClick={handleInstall}
            className="mt-1 text-xs font-semibold text-accent-primary hover:text-accent-primary-hover transition-colors"
          >
            {t('pwa.install.cta')}
          </button>
        </div>

        <button
          onClick={handleDismiss}
          aria-label={t('pwa.install.dismiss')}
          className="flex-shrink-0 text-text-muted hover:text-text-secondary transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
